"use client";

import React, { useEffect, useState } from "react";
import { createBrowserClient } from "@/utils/supabase/client";
import { usePathname } from "next/navigation";
import { Crisp } from "crisp-sdk-web";
import NextTopLoader from "nextjs-toploader";
import { Toaster } from "react-hot-toast";
import { Tooltip } from "react-tooltip";
import { ThemeProvider } from "next-themes";
import { User } from "@supabase/supabase-js";
import config from "@/config";
import { AccountRefreshProvider } from "@/components/accounts/AccountRefreshContext";
import AuthStatusIndicator from "@/components/AuthStatusIndicator";
import { QuickLoginProvider } from "@/components/QuickLoginContext";
import QuickLoginModal from "@/components/QuickLoginModal";
import AchievementToaster from "@/components/social/AchievementToaster";
import { AnalyticsProvider } from "@/utils/analytics/provider";
import CookieConsent from "@/components/privacy/CookieConsent";

// Crisp customer chat support:
// This component is separated from ClientLayout because it needs to be wrapped with <SessionProvider> to use useSession() hook
const CrispChat: React.FC = () => {
  const pathname = usePathname();

  const supabase = createBrowserClient();
  const [data, setData] = useState<User | null>(null);

  // This is used to get the user data from Supabase Auth (if logged in) => user ID is used to identify users in Crisp
  useEffect(() => {
    const fetchUser = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (user) {
        setData(user);
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    if (config?.crisp?.id) {
      // Set up Crisp
      Crisp.configure(config.crisp.id);

      // (Optional) If onlyShowOnRoutes array is not empty in config.js file, Crisp will be hidden on the routes in the array.
      // Use <AppButtonSupport> instead to show it (user clicks on the button to show Crisp—it cleans the UI)
      if (
        config.crisp.onlyShowOnRoutes &&
        !config.crisp.onlyShowOnRoutes?.includes(pathname)
      ) {
        Crisp.chat.hide();
        Crisp.chat.onChatClosed(() => {
          Crisp.chat.hide();
        });
      }
    }
  }, [pathname]);

  // Add User Unique ID to Crisp to easily identify users when reaching support (optional)
  useEffect(() => {
    if (data && config?.crisp?.id) {
      Crisp.session.setData({ userId: data.id });
    }
  }, [data]);

  return null;
};

// All the client wrappers are here (they can't be in server components)
// 1. NextTopLoader: Show a progress bar at the top when navigating between pages
// 2. Toaster: Show Success/Error messages anywhere from the app with toast()
// 3. Tooltip: Show tooltips if any JSX elements has these 2 attributes: data-tooltip-id="tooltip" data-tooltip-content=""
// 4. CrispChat: Set Crisp customer chat support (see above)
// 5. ThemeProvider: Manage light/dark mode themes with next-themes
interface ClientLayoutProps {
  children: React.ReactNode;
}

const ClientLayout: React.FC<ClientLayoutProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const supabase = createBrowserClient();

  useEffect(() => {
    const fetchUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
    };
    fetchUser();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [supabase]);

  return (
    // Drives BOTH the .dark class (design tokens) and data-theme (DaisyUI)
    // so the two theming systems can never disagree
    <ThemeProvider
      attribute={["class", "data-theme"]}
      defaultTheme="system"
      enableSystem
    >
      <AnalyticsProvider userId={user?.id} userTraits={user ? {} : undefined}>
        <QuickLoginProvider>
          <AccountRefreshProvider>
            {/* Show a progress bar at the top when navigating between pages */}
            <NextTopLoader color={config.colors.main} showSpinner={false} />

            {/* Content inside app/page.js files  */}
            {children}

            {/* Show Success/Error messages anywhere from the app with toast() */}
            <Toaster
          toastOptions={{
            duration: 3000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              duration: 4000,
              iconTheme: {
                primary: '#06FB08',
                secondary: '#fff',
              },
              ariaProps: {
                role: 'status',
                'aria-live': 'polite',
              },
            },
            error: {
              duration: 5000,
              iconTheme: {
                primary: '#ff4b4b',
                secondary: '#fff',
              },
              ariaProps: {
                role: 'alert',
                'aria-live': 'assertive',
              },
            },
          }}
        />

        {/* Show tooltips if any JSX elements has these 2 attributes: data-tooltip-id="tooltip" data-tooltip-content="" */}
        <Tooltip
          id="tooltip"
          className="z-[60] !opacity-100 max-w-sm shadow-lg"
        />

            {/* Set Crisp customer chat support */}
            <CrispChat />
            
            {/* Auth status indicator */}
            <AuthStatusIndicator />
            
            {/* Quick login modal */}
            <QuickLoginModal />

            {/* Achievement unlock toasts (no-ops quietly when logged out) */}
            <AchievementToaster />

            {/* Cookie consent banner */}
            <CookieConsent />
          </AccountRefreshProvider>
        </QuickLoginProvider>
      </AnalyticsProvider>
    </ThemeProvider>
  );
};

export default ClientLayout;
